import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authClient } from './lib/auth-client';
import { usePostHogIdentify, usePostHogPageviews } from './hooks/usePostHog';
import Layout from './components/Layout';
import FirstRunGate from './components/FirstRunGate';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import WeddingsPage from './pages/WeddingsPage';
import WeddingDetailPage from './pages/WeddingDetailPage';
import ComposePage from './pages/ComposePage';
import BatchProgressPage from './pages/BatchProgressPage';
import InquiryDetailPage from './pages/InquiryDetailPage';
import DashboardPage from './pages/DashboardPage';
import ReviewPage from './pages/ReviewPage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import WelcomePage from './pages/WelcomePage';
import SettingsPage from './pages/SettingsPage';
import TemplatesPage from './pages/TemplatesPage';
import { Toaster } from '@/components/ui/sonner';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="display-tight text-lg text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;

  // Authenticated is not the same as ready. The gate is inside the session
  // check because it reads an endpoint that needs one, and outside `Layout`
  // because nav chrome around a spinner is chrome around nothing.
  return (
    <FirstRunGate>
      <Layout>{children}</Layout>
    </FirstRunGate>
  );
}

function PostHogTracker() {
  usePostHogIdentify();
  usePostHogPageviews();
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PostHogTracker />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* PRD §F7: the dashboard is the post-login default route. */}
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />

          <Route path="/weddings" element={<ProtectedRoute><WeddingsPage /></ProtectedRoute>} />
          <Route path="/weddings/:id" element={<ProtectedRoute><WeddingDetailPage /></ProtectedRoute>} />
          <Route path="/weddings/:id/compose" element={<ProtectedRoute><ComposePage /></ProtectedRoute>} />
          <Route path="/weddings/:id/batches/:batchId" element={<ProtectedRoute><BatchProgressPage /></ProtectedRoute>} />
          <Route path="/inquiries/:id" element={<ProtectedRoute><InquiryDetailPage /></ProtectedRoute>} />
          <Route path="/review" element={<ProtectedRoute><ReviewPage /></ProtectedRoute>} />
          <Route path="/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
          <Route path="/vendors/:id" element={<ProtectedRoute><VendorDetailPage /></ProtectedRoute>} />
          <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  );
}
